import { ref, computed, markRaw, type Component } from 'vue';
import BattleplanSurface from './BattleplanSurface.vue';
import BattleTacticsSurface from './BattleTacticsSurface.vue';
import RulesSurface from './RulesSurface.vue';
import ArmySurface from './ArmySurface.vue';
import RegimentsOfRenownSurface from './RegimentsOfRenownSurface.vue';
import SettingsSurface from './SettingsSurface.vue';
import { isGated } from '../env.js';

/**
 * An editor "surface" is a top-level workspace the studio shell can switch to
 * via the activity rail. Today: battleplans + settings. Future homebrew
 * editors (units, enhancements, abilities, full battlepacks) register here as
 * additional entries — each a self-contained component reusing the shell,
 * the `--st-*` design system, and the shared workspace/library state.
 */
export interface EditorSurface {
  id: string;
  label: string;
  /** Font Awesome solid icon name for the activity rail. */
  icon: string;
  component: Component;
  /** Push to the bottom of the rail (e.g. settings). */
  footer?: boolean;
  /** Hidden in the gated (production) config; available in dev only. */
  devOnly?: boolean;
  /**
   * Usable without an active bundle. Editor surfaces author into the bundle-backed
   * workspace, so they're gated behind a bundle (see BundleGate); standalone
   * surfaces like Settings stay reachable regardless.
   */
  standalone?: boolean;
}

const SURFACE_KEY = 'studio:activeSurface';

const allSurfaces: EditorSurface[] = [
  {
    id: 'battleplans',
    label: 'Battleplans',
    icon: 'vector-square',
    component: markRaw(BattleplanSurface),
  },
  {
    id: 'battle-tactics',
    label: 'Battle Tactics',
    icon: 'chess-knight',
    component: markRaw(BattleTacticsSurface),
    devOnly: true,
  },
  {
    id: 'rules',
    label: 'Rules',
    icon: 'book-bookmark',
    component: markRaw(RulesSurface),
  },
  {
    id: 'armies',
    label: 'Armies',
    icon: 'shield-halved',
    component: markRaw(ArmySurface),
    devOnly: true,
  },
  {
    id: 'regiments-of-renown',
    label: 'Regiments of Renown',
    icon: 'sitemap',
    component: markRaw(RegimentsOfRenownSurface),
    devOnly: true,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'gear',
    component: markRaw(SettingsSurface),
    footer: true,
    standalone: true,
  },
];

// In the gated (production) config, drop the dev-only editors from the rail.
export const surfaces: EditorSurface[] = allSurfaces.filter((s) => !isGated || !s.devOnly);

const activeId = ref<string>(
  (() => {
    const saved = localStorage.getItem(SURFACE_KEY);
    return surfaces.some((s) => s.id === saved) ? (saved as string) : surfaces[0].id;
  })(),
);

const activeSurface = computed(() => surfaces.find((s) => s.id === activeId.value) ?? surfaces[0]);

function setActive(id: string) {
  if (surfaces.some((s) => s.id === id)) {
    activeId.value = id;
    localStorage.setItem(SURFACE_KEY, id);
  }
}

export function useSurfaces() {
  return { surfaces, activeId, activeSurface, setActive };
}
